import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from '../styles/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // 尝试从localStorage获取主题设置
    try {
      const savedTheme = localStorage.getItem('theme');
      // 检测系统暗色模式偏好
      const prefersDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      // 如果没有保存的主题设置，则使用系统偏好
      return savedTheme ? savedTheme === 'dark' : prefersDarkMode;
    } catch (error) {
      console.error('获取主题设置失败:', error);
      return false;
    }
  });

  // 监听系统主题变化
  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        const userSetTheme = localStorage.getItem('theme');
        // 只有当用户没有明确设置主题时，才跟随系统变化
        if (!userSetTheme) {
          setIsDarkMode(e.matches);
        }
      };

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
      } else {
        // 为旧版浏览器提供支持
        mediaQuery.addListener(handleChange);
      }

      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleChange);
        } else {
          mediaQuery.removeListener(handleChange);
        }
      };
    } catch (error) {
      console.error('监听系统主题变化失败:', error);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
      // 设置HTML属性以便CSS可以使用
      document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    } catch (error) {
      console.error('保存主题设置失败:', error);
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, theme }}>
      <StyledThemeProvider theme={{ ...theme, isDarkMode }}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
}; 